// Wire codec — mirrors crates/wire/src/codec.rs byte-for-byte.
//
// Big-endian multi-byte integers, length-prefixed byte sequences, and a
// header block matching  Frame envelope:
//
//   length (u32 BE)   // total payload length, including type + flags
//   type   (u8)
//   flags  (u8)
//   payload (length-2 bytes)

import {
  FrameTooLargeError,
  FrameTooShortError,
  InvalidStatusError,
  TruncatedPayloadError,
  UnknownWsOpcodeError,
} from "./errors.js";
import {
  FrameType,
  MAX_FRAME_BODY,
  isKnownFrameType,
  isKnownWsOpcode,
  type Frame,
  type Header,
  type Headers,
  type WsOpcodeByte,
} from "./frame.js";

// ---------- encode ----------

/**
 * Serialize a frame to a fresh Uint8Array.
 *
 * The Rust side appends to a `BytesMut` buffer; the TypeScript side
 * returns a standalone buffer — callers that want to write multiple
 * frames into one buffer can concatenate the results.
 */
export function encode(frame: Frame): Uint8Array {
  const w = new Writer();
  // Reserve length prefix; patch once payload length is known.
  w.putU32(0);
  const payloadStart = w.length;

  switch (frame.type) {
    case "req":
      w.putHeader(FrameType.Req, frame.flags);
      w.putU32(frame.streamId);
      w.putLpU16(frame.method);
      w.putLpU16(frame.path);
      w.putHeaders(frame.headers);
      break;
    case "res":
      w.putHeader(FrameType.Res, frame.flags);
      w.putU32(frame.streamId);
      w.putU16(frame.status);
      w.putHeaders(frame.headers);
      break;
    case "data":
      w.putHeader(FrameType.Data, frame.flags);
      w.putU32(frame.streamId);
      w.putBytes(frame.body);
      break;
    case "end":
      w.putHeader(FrameType.End, 0);
      w.putU32(frame.streamId);
      break;
    case "err":
      w.putHeader(FrameType.Err, 0);
      w.putU32(frame.streamId);
      w.putU16(frame.code);
      w.putLpU16(frame.message);
      break;
    case "trailers":
      // Layout: stream_id (u32) + headers block. No status, no
      // flags semantics — TRAILERS implicitly carries end-of-stream
      // so the only valid flags byte is 0.
      w.putHeader(FrameType.Trailers, 0);
      w.putU32(frame.streamId);
      w.putHeaders(frame.headers);
      break;
    case "ws_upgrade":
      w.putHeader(FrameType.WsUpgrade, 0);
      w.putU32(frame.streamId);
      w.putLpU16(frame.path);
      w.putHeaders(frame.headers);
      break;
    case "ws_accept":
      w.putHeader(FrameType.WsAccept, 0);
      w.putU32(frame.streamId);
      w.putHeaders(frame.headers);
      break;
    case "ws_msg":
      w.putHeader(FrameType.WsMsg, 0);
      w.putU32(frame.streamId);
      w.putU8(frame.opcode);
      w.putBytes(frame.payload);
      break;
    case "ws_close":
      w.putHeader(FrameType.WsClose, 0);
      w.putU32(frame.streamId);
      w.putU16(frame.code);
      w.putLpU16(frame.reason);
      break;
    case "ping":
      w.putHeader(FrameType.Ping, 0);
      assertNonce(frame.nonce);
      w.putBytes(frame.nonce);
      break;
    case "pong":
      w.putHeader(FrameType.Pong, 0);
      assertNonce(frame.nonce);
      w.putBytes(frame.nonce);
      break;
    case "goaway":
      w.putHeader(FrameType.Goaway, 0);
      w.putU32(frame.lastAcceptedStreamId);
      w.putU16(frame.code);
      w.putLpU16(frame.message);
      break;
    case "probe":
      // header + id + opaque payload bytes.
      w.putHeader(FrameType.Probe, frame.flags);
      w.putU32(frame.id);
      w.putBytes(frame.payload);
      break;
    case "probe_ack":
      // header + id, no further payload.
      w.putHeader(FrameType.ProbeAck, frame.flags);
      w.putU32(frame.id);
      break;
    case "unknown":
      // We never *encode* an unknown frame — those are exclusively
      // a decoder-side sentinel for forward-compat parsing. Throwing
      // here surfaces the bug at the source rather than re-emitting
      // a frame we don't have semantics for.
      throw new Error(
        `wire: cannot encode unknown frame (typeByte=0x${frame.typeByte
          .toString(16)
          .padStart(2, "0")})`,
      );
  }

  const payloadLen = w.length - payloadStart;
  w.patchU32(0, payloadLen);
  return w.toBytes();
}

function assertNonce(n: Uint8Array): void {
  if (n.length !== 8) {
    throw new TruncatedPayloadError(8, n.length);
  }
}

// ---------- decode ----------

export interface DecodeResult {
  frame: Frame;
  consumed: number;
}

/**
 * Try to decode one frame from the front of `buf`.
 *
 * Returns `null` if more bytes are needed. Throws a `WireError`
 * subclass on malformed input; the caller should treat the connection
 * as poisoned on any throw.
 *
 * `consumed` is the number of bytes to advance past once the caller has
 * processed the returned frame.
 */
export function decode(buf: Uint8Array): DecodeResult | null {
  if (buf.length < 4) {
    return null;
  }
  const frameLen = readU32(buf, 0);

  if (frameLen < 2) {
    throw new FrameTooShortError(frameLen);
  }
  if (frameLen > MAX_FRAME_BODY) {
    throw new FrameTooLargeError(frameLen, MAX_FRAME_BODY);
  }
  const total = 4 + frameLen;
  if (buf.length < total) {
    return null;
  }

  const r = new Reader(buf, 4, total);
  const typeByte = r.takeU8();
  const flags = r.takeU8();

  let frame: Frame;
  // forward-compat. Unknown FrameType bytes don't throw any
  // more; they surface as a synthetic `unknown` frame so the
  // translator's drain loop can log + advance instead of tearing
  // down the data channel. This is what lets the box's chunk-cap
  // probe rollout ship without breaking older bootstraps,
  // and closes the same gap permanently for future wire
  // extensions.
  if (!isKnownFrameType(typeByte)) {
    const payload = r.takeRest();
    return {
      frame: { type: "unknown", typeByte, flags, payload },
      consumed: total,
    };
  }
  switch (typeByte) {
    case FrameType.Req:
      frame = decodeReq(r, flags);
      break;
    case FrameType.Res:
      frame = decodeRes(r, flags);
      break;
    case FrameType.Data:
      frame = decodeData(r, flags);
      break;
    case FrameType.End:
      frame = decodeEnd(r);
      break;
    case FrameType.Err:
      frame = decodeErr(r);
      break;
    case FrameType.Trailers:
      frame = decodeTrailers(r);
      break;
    case FrameType.WsUpgrade:
      frame = decodeWsUpgrade(r);
      break;
    case FrameType.WsAccept:
      frame = decodeWsAccept(r);
      break;
    case FrameType.WsMsg:
      frame = decodeWsMsg(r);
      break;
    case FrameType.WsClose:
      frame = decodeWsClose(r);
      break;
    case FrameType.Ping:
      frame = decodePing(r);
      break;
    case FrameType.Pong:
      frame = decodePong(r);
      break;
    case FrameType.Probe:
      frame = decodeProbe(r, flags);
      break;
    case FrameType.ProbeAck:
      frame = decodeProbeAck(r, flags);
      break;
    case FrameType.Goaway:
      frame = decodeGoaway(r);
      break;
  }

  return { frame, consumed: total };
}

// ---------- per-type decoders ----------

function decodeReq(r: Reader, flags: number): Frame {
  const streamId = r.takeU32();
  const method = r.takeLpU16();
  const path = r.takeLpU16();
  const headers = r.takeHeaders();
  return { type: "req", streamId, flags, method, path, headers };
}

function decodeRes(r: Reader, flags: number): Frame {
  const streamId = r.takeU32();
  const status = r.takeU16();
  if (status < 100 || status > 599) {
    throw new InvalidStatusError(status);
  }
  const headers = r.takeHeaders();
  return { type: "res", streamId, flags, status, headers };
}

function decodeData(r: Reader, flags: number): Frame {
  const streamId = r.takeU32();
  const body = r.takeRest();
  return { type: "data", streamId, flags, body };
}

function decodeEnd(r: Reader): Frame {
  const streamId = r.takeU32();
  return { type: "end", streamId };
}

function decodeErr(r: Reader): Frame {
  const streamId = r.takeU32();
  const code = r.takeU16();
  const message = r.takeLpU16();
  return { type: "err", streamId, code, message };
}

function decodeTrailers(r: Reader): Frame {
  const streamId = r.takeU32();
  const headers = r.takeHeaders();
  return { type: "trailers", streamId, headers };
}

function decodeWsUpgrade(r: Reader): Frame {
  const streamId = r.takeU32();
  const path = r.takeLpU16();
  const headers = r.takeHeaders();
  return { type: "ws_upgrade", streamId, path, headers };
}

function decodeWsAccept(r: Reader): Frame {
  const streamId = r.takeU32();
  const headers = r.takeHeaders();
  return { type: "ws_accept", streamId, headers };
}

function decodeWsMsg(r: Reader): Frame {
  const streamId = r.takeU32();
  if (r.remaining < 1) {
    throw new TruncatedPayloadError(1, 0);
  }
  const opcodeByte = r.takeU8();
  if (!isKnownWsOpcode(opcodeByte)) {
    throw new UnknownWsOpcodeError(opcodeByte);
  }
  const opcode: WsOpcodeByte = opcodeByte;
  const payload = r.takeRest();
  return { type: "ws_msg", streamId, opcode, payload };
}

function decodeWsClose(r: Reader): Frame {
  const streamId = r.takeU32();
  const code = r.takeU16();
  const reason = r.takeLpU16();
  return { type: "ws_close", streamId, code, reason };
}

function decodePing(r: Reader): Frame {
  if (r.remaining < 8) {
    throw new TruncatedPayloadError(8, r.remaining);
  }
  return { type: "ping", nonce: r.takeExact(8) };
}

function decodePong(r: Reader): Frame {
  if (r.remaining < 8) {
    throw new TruncatedPayloadError(8, r.remaining);
  }
  return { type: "pong", nonce: r.takeExact(8) };
}

function decodeGoaway(r: Reader): Frame {
  const lastAcceptedStreamId = r.takeU32();
  const code = r.takeU16();
  const message = r.takeLpU16();
  return { type: "goaway", lastAcceptedStreamId, code, message };
}

function decodeProbe(r: Reader, flags: number): Frame {
  const id = r.takeU32();
  // Payload is opaque — could be ~8 KiB on the production path or
  // a single byte in a test fixture. The wrapper doesn't care
  // about the contents; the ack only echoes `id`.
  const payload = r.takeRest();
  return { type: "probe", flags, id, payload };
}

function decodeProbeAck(r: Reader, flags: number): Frame {
  const id = r.takeU32();
  // No further payload. Any trailing bytes would be a protocol
  // violation — silently swallow rather than throw, consistent with
  // the forward-compat stance for unknown frame types.
  return { type: "probe_ack", flags, id };
}

// ---------- internal helpers ----------

function readU32(buf: Uint8Array, offset: number): number {
  // Unsigned 32-bit big-endian. Use arithmetic so the result is always
  // a safe integer (bitwise `|` in JS sign-extends above 2^31).
  const b0 = buf[offset] ?? 0;
  const b1 = buf[offset + 1] ?? 0;
  const b2 = buf[offset + 2] ?? 0;
  const b3 = buf[offset + 3] ?? 0;
  return b0 * 0x01000000 + ((b1 << 16) | (b2 << 8) | b3);
}

/** Growable byte writer backed by a Uint8Array. */
class Writer {
  private buf: Uint8Array;
  private len = 0;

  constructor(initialCapacity = 64) {
    this.buf = new Uint8Array(initialCapacity);
  }

  get length(): number {
    return this.len;
  }

  private ensure(additional: number): void {
    const need = this.len + additional;
    if (need <= this.buf.length) return;
    let cap = this.buf.length === 0 ? 16 : this.buf.length;
    while (cap < need) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
  }

  putU8(v: number): void {
    this.ensure(1);
    this.buf[this.len++] = v & 0xff;
  }

  putU16(v: number): void {
    this.ensure(2);
    this.buf[this.len++] = (v >>> 8) & 0xff;
    this.buf[this.len++] = v & 0xff;
  }

  putU32(v: number): void {
    // Split into two 16-bit halves to avoid sign issues with values ≥ 2^31.
    this.ensure(4);
    const hi = Math.floor(v / 0x10000) & 0xffff;
    const lo = v & 0xffff;
    this.buf[this.len++] = (hi >>> 8) & 0xff;
    this.buf[this.len++] = hi & 0xff;
    this.buf[this.len++] = (lo >>> 8) & 0xff;
    this.buf[this.len++] = lo & 0xff;
  }

  putBytes(src: Uint8Array): void {
    this.ensure(src.length);
    this.buf.set(src, this.len);
    this.len += src.length;
  }

  putHeader(type: number, flags: number): void {
    this.putU8(type);
    this.putU8(flags);
  }

  putLpU16(src: Uint8Array): void {
    this.putU16(src.length);
    this.putBytes(src);
  }

  putHeaders(headers: Headers): void {
    this.putU16(headers.length);
    for (const [name, value] of headers) {
      this.putLpU16(name);
      this.putLpU16(value);
    }
  }

  /** Patch a 4-byte big-endian integer at `offset`. */
  patchU32(offset: number, value: number): void {
    const hi = Math.floor(value / 0x10000) & 0xffff;
    const lo = value & 0xffff;
    this.buf[offset] = (hi >>> 8) & 0xff;
    this.buf[offset + 1] = hi & 0xff;
    this.buf[offset + 2] = (lo >>> 8) & 0xff;
    this.buf[offset + 3] = lo & 0xff;
  }

  toBytes(): Uint8Array {
    // Return a standalone slice so the caller can mutate the result
    // without stomping on the growable buffer.
    return this.buf.slice(0, this.len);
  }
}

/** Cursor over a payload slice. */
class Reader {
  private pos: number;
  constructor(
    private readonly buf: Uint8Array,
    start: number,
    private readonly end: number,
  ) {
    this.pos = start;
  }

  get remaining(): number {
    return this.end - this.pos;
  }

  takeU8(): number {
    if (this.remaining < 1) {
      throw new TruncatedPayloadError(1, this.remaining);
    }
    return this.buf[this.pos++]!;
  }

  takeU16(): number {
    if (this.remaining < 2) {
      throw new TruncatedPayloadError(2, this.remaining);
    }
    const hi = this.buf[this.pos]!;
    const lo = this.buf[this.pos + 1]!;
    this.pos += 2;
    return (hi << 8) | lo;
  }

  takeU32(): number {
    if (this.remaining < 4) {
      throw new TruncatedPayloadError(4, this.remaining);
    }
    const b0 = this.buf[this.pos]!;
    const b1 = this.buf[this.pos + 1]!;
    const b2 = this.buf[this.pos + 2]!;
    const b3 = this.buf[this.pos + 3]!;
    this.pos += 4;
    return b0 * 0x01000000 + ((b1 << 16) | (b2 << 8) | b3);
  }

  /**
   * Take exactly `n` bytes as a copy. Throws if fewer than `n` remain.
   */
  takeExact(n: number): Uint8Array {
    if (this.remaining < n) {
      throw new TruncatedPayloadError(n, this.remaining);
    }
    const out = this.buf.slice(this.pos, this.pos + n);
    this.pos += n;
    return out;
  }

  takeLpU16(): Uint8Array {
    const len = this.takeU16();
    return this.takeExact(len);
  }

  takeHeaders(): Headers {
    const count = this.takeU16();
    const out: Header[] = [];
    for (let i = 0; i < count; i++) {
      const name = this.takeLpU16();
      const value = this.takeLpU16();
      out.push([name, value]);
    }
    return out;
  }

  takeRest(): Uint8Array {
    const out = this.buf.slice(this.pos, this.end);
    this.pos = this.end;
    return out;
  }
}
